import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useMyTasks, type MyTask } from "@/hooks/use-my-tasks";
import { useProjectCommentUnread } from "@/hooks/use-project-comment-reads";
import { ProjectUpdatesDialog } from "@/components/project/ProjectUpdatesDialog";
import TaskMobileCard from "./TaskMobileCard";
import { usePersistedCollapse } from "@/hooks/use-persisted-collapse";
import type { SectionKey } from "@/lib/section-collapse-store";
import { MyTasksRow } from "./MyTasksRow";
import { MyTasksProjectGroup } from "./MyTasksProjectGroup";
import { MyTasksListHeader } from "./MyTasksListHeader";

interface MyTasksListProps {
  projectId?: string;
  showProject?: boolean;
  defaultCollapsed?: boolean;
  title?: string;
  /**
   * VIB-2100: satt = åpen/lukket-tilstand huskes per bruker under denne
   * nøkkelen. Listen står både på Mine oppgaver og på et prosjekts oversikt,
   * og de to skal ikke dele tilstand — derfor bestemmer kallestedet, ikke
   * komponenten. Utelatt = flyktig tilstand, som før.
   */
  persistKey?: SectionKey;
}

const STATUS_ORDER: Record<string, number> = { in_progress: 0, not_started: 1 };
// Feedback (Sigurd): oppgaver skal vises i budsjett-/fase-rekkefølge
// (pre → prod → post → reise), ikke status-først.
const PHASE_ORDER: Record<string, number> = { preprod: 0, production: 1, postprod: 2, reise: 3 };

/**
 * Orchestrator for the "Mine oppgaver" list. Fetches tasks for the active
 * (possibly impersonated) user, filters by project when scoped, sorts
 * in_progress first, and renders a desktop table grouped by project or a
 * mobile card list depending on viewport.
 */
export function MyTasksList({
  projectId,
  showProject,
  defaultCollapsed = false,
  title = "Mine oppgaver",
  persistKey,
}: MyTasksListProps) {
  const { activeUser } = useImpersonation();
  const { data } = useMyTasks(activeUser?.id ?? null);
  const [expanded, setExpanded] = usePersistedCollapse(
    persistKey ?? null,
    !defaultCollapsed,
  );
  const navigate = useNavigate();

  const showProjectCol = showProject ?? !projectId;

  const allTasks = (data?.tasks ?? []).filter(
    (t) =>
      ["in_progress", "not_started"].includes(t.task_status) &&
      (!projectId || t.project_id === projectId)
  );

  // Fase først (budsjett-rekkefølge), så status (pågår før ikke-startet) innen
  // samme fase. Grupperingen under bevarer denne rekkefølgen per prosjekt.
  const sortedTasks = [...allTasks].sort((a, b) => {
    const ph = (PHASE_ORDER[a.phase ?? ""] ?? 9) - (PHASE_ORDER[b.phase ?? ""] ?? 9);
    if (ph !== 0) return ph;
    return (STATUS_ORDER[a.task_status] ?? 9) - (STATUS_ORDER[b.task_status] ?? 9);
  });

  const tasksByProject = showProjectCol
    ? Array.from(
        sortedTasks.reduce((map, t) => {
          const list = map.get(t.project_id) ?? [];
          list.push(t);
          map.set(t.project_id, list);
          return map;
        }, new Map<string, MyTask[]>())
      )
    : [];

  const inProgress = sortedTasks.filter((t) => t.task_status === "in_progress");

  // VIB-2180: oppdateringsikon + ulest-badge på prosjektgruppene.
  // Én RPC for alle prosjektene i lista (hooken dedupliserer ID-ene), aldri
  // ett kall per gruppe. Kun relevant når lista er gruppert per prosjekt.
  const updatesEnabled = showProjectCol;
  const { data: unreadMap } = useProjectCommentUnread(
    sortedTasks.map((t) => t.project_id),
    { enabled: updatesEnabled },
  );
  const [updatesProject, setUpdatesProject] = useState<{ id: string; name: string } | null>(null);
  const openUpdates = updatesEnabled
    ? (pid: string) => {
        const name = sortedTasks.find((t) => t.project_id === pid)?.project_name ?? "";
        setUpdatesProject({ id: pid, name });
      }
    : undefined;

  // VIB-2546: tom-tilstand i stedet for å forsvinne sporløst (samme mønster
  // som TimebankCard «Ingen søknader registrert ennå»).
  if (sortedTasks.length === 0) {
    return (
      <div className="border-[1.5px] border-foreground bg-card px-4 py-6 text-center">
        <p className="text-sm text-muted-foreground">Ingen tildelte oppgaver akkurat nå.</p>
      </div>
    );
  }

  const userId = activeUser?.id ?? null;
  const colSpan = showProjectCol ? 6 : 5;

  return (
    <div id="tasks-table" className="border-[1.5px] border-foreground bg-card">
      <MyTasksListHeader
        title={title}
        totalCount={sortedTasks.length}
        inProgressCount={inProgress.length}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
      />

      {expanded && (
        <div className="border-t">
          {/* Desktop: table */}
          <div className="hidden xl:block">
            <table className="w-full text-sm">
              {/* Only show column headers when NOT grouping by project */}
              {!(showProjectCol && tasksByProject.length > 0) && (
                <thead>
                  <tr className="text-muted-foreground text-xs uppercase tracking-wider border-b">
                    {showProjectCol && <th className="text-left py-2 px-4 font-medium">Prosjekt</th>}
                    <th className="text-left py-2 px-4 font-medium">Oppgave</th>
                    <th className="text-right py-2 px-4 font-medium">Timer</th>
                    <th className="text-center py-2 px-4 font-medium">Tidssporing</th>
                    <th className="text-center py-2 px-4 font-medium">Status</th>
                    {showProjectCol && <th className="py-2 px-4 w-8" />}
                  </tr>
                </thead>
              )}
              <tbody>
                {showProjectCol && tasksByProject.length > 0
                  ? tasksByProject.map(([pid, tasks]) => (
                    <MyTasksProjectGroup
                      key={pid}
                      projectId={pid}
                      projectName={tasks[0].project_name}
                      tasks={tasks}
                      userId={userId}
                      colSpan={colSpan}
                      onNavigate={(p) => navigate(`/prosjekter/${p}`)}
                      unread={unreadMap?.get(pid)}
                      onOpenUpdates={openUpdates}
                    />
                  ))
                  : sortedTasks.map((t) => (
                    <MyTasksRow
                      key={t.id}
                      task={t}
                      showProject={showProjectCol}
                      userId={userId}
                      onNavigate={(p) => navigate(`/prosjekter/${p}`)}
                    />
                  ))
                }
              </tbody>
            </table>
          </div>

          {/* Mobile: card list */}
          <div className="xl:hidden">
            {sortedTasks.map((t) => (
              <TaskMobileCard
                key={t.id}
                task={t}
                showProject={showProjectCol}
                userId={userId}
                onNavigate={(p) => navigate(`/prosjekter/${p}`)}
                unread={unreadMap?.get(t.project_id)}
                onOpenUpdates={openUpdates}
              />
            ))}
          </div>
        </div>
      )}

      {updatesProject && (
        <ProjectUpdatesDialog
          projectId={updatesProject.id}
          projectName={updatesProject.name}
          open={!!updatesProject}
          onOpenChange={(open) => {
            if (!open) setUpdatesProject(null);
          }}
        />
      )}
    </div>
  );
}

export default MyTasksList;
